<template>
    <div class="retr-list">
        <accordion
            v-for="retrItem in retrCurrentList"
            :key="retrItem.contract_group"
            class="retr mt-3"
            :is-active="openedRetrs.includes(retrItem.contract_group)"
            :show-chevron="retrItemsWithRetroValueMap[retrItem.contract_group]"
            @header-click="onHeaderClick(retrItem)"
        >
            <template #header>
                <div class="retr__header cursor-pointer">
                    <div class="retr__header-name">
                        <a-icon class="text-orange" icon="Coins" />
                        {{ retrItem.contract_group }}
                    </div>
                    <div class="retr__header-value">
                        <template v-if="retrItemsWithRetroValueMap[retrItem.contract_group]">
                            {{ retrItem.retro | currency }}
                        </template>
                        <template v-else>
                            -
                        </template>
                    </div>
                </div>
            </template>

            <template #expand-button>
                <div
                    v-if="retrItemsWithRetroValueMap[retrItem.contract_group]"
                    class="d-flex align-items-center cursor-pointer"
                >
                    <span class="retr__expand-text text-sm font-weight-normal">
                        Szczegóły
                    </span>

                    <a-icon
                        class="a-accordion__chevron ml-0"
                        icon="Chevron"
                    />
                </div>
            </template>

            <template #body>
                <retr-current-single-table
                    :ref="`table-${retrItem.contract_group}`"
                    :retr="retrItem"
                    :selected-manufacturer-name="selectedManufacturerName"
                />
            </template>
        </accordion>

        <div class="retr card">
            <div class="retr__sum">
                <div class="retr__header-name">
                    <a-icon class="text-orange" icon="Sum" />
                    Suma wszystkich premii
                </div>
                <div class="retr__header-value">
                    {{ retrSumValue | currency }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import RetrCurrentSingleTable from '@/components/my-business/retr/RetrCurrentSingleTable.vue'

export default {
    components: {
        RetrCurrentSingleTable
    },
    props: {
        retr: {
            type: Array,
            required: true
        },
        selectedManufacturerName: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            openedRetrs: [] // Array of contract_group names that are currently expanded
        }
    },

    computed: {
        retrCurrentList() {
            if (!this.retr || !Array.isArray(this.retr)) {
                return []
            }

            // Simply sort by order, no need to map
            return [...this.retr].sort((a, b) => (a.order || 0) - (b.order || 0))
        },

        /**
         * Cached map of contract_group -> hasRetroValue boolean
         * Computed once instead of calling method multiple times per item in template
         */
        retrItemsWithRetroValueMap() {
            return this.retrCurrentList.reduce((acc, retrItem) => {
                acc[retrItem.contract_group] = this.hasRetroValue(retrItem)
                return acc
            }, {})
        },

        retrSumValue() {
            return this.retrCurrentList.reduce((acc, el) => {
                return this.hasRetroValue(el) ? acc + el.retro : acc
            }, 0)
        }
    },

    methods: {
        hasRetroValue(retrData) {
            return typeof retrData.retro === 'number' && !isNaN(retrData.retro)
        },

        onHeaderClick(retrItem) {
            if (this.hasRetroValue(retrItem)) {
                const contractGroup = retrItem.contract_group
                this.openedRetrs = this.openedRetrs.includes(contractGroup)
                    ? this.openedRetrs.filter(cg => cg !== contractGroup)
                    : [...this.openedRetrs, contractGroup]
            }
        },

        /**
         * Trigger auto-expand - called once from parent after data load
         * This is PUBLIC method exposed to parent component
         */
        triggerAutoExpand() {
            // Reset to close all accordions first
            this.openedRetrs = []

            // Only auto-expand when manufacturer filter is active
            if (!this.selectedManufacturerName || this.retrCurrentList.length === 0) {
                return
            }

            const firstRetr = this.retrCurrentList[0]
            const contractGroup = firstRetr.contract_group

            // Check if this contract group contains the manufacturer we're looking for
            const manufacturerNameLower = this.selectedManufacturerName.toLowerCase()
            const containsManufacturer = (firstRetr.list || []).some(({ manufacturer_name = '' }) =>
                manufacturer_name.toLowerCase() === manufacturerNameLower
            )

            if (this.hasRetroValue(firstRetr) && containsManufacturer) {
                this.openedRetrs = [contractGroup]

                // Also trigger auto-expand in child table component
                this.$nextTick(() => {
                    const tableRefKey = `table-${contractGroup}`
                    const tableComponent = this.$refs[tableRefKey]

                    if (tableComponent && tableComponent[0]) {
                        tableComponent[0].triggerAutoExpand()
                    }
                })
            }
        }
    }
}
</script>

<style lang="scss">
@import '../../../styles/_custom.scss';
@import '~bootstrap/scss/mixins';
@import '../../../styles/components/retr-common';

.retr-list {
    .a-accordion__header {
        padding-right: 20px !important;
    }
}

.retr {
    --button-space: 87px;

    .clientBudget {
        .retr {
            &__value {
                font-weight: 700;
            }
        }
    }

    &__header, &__sum {
        white-space: normal;
        text-align: left;
        display: flex;
        gap: 1rem;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }

    &__header-name {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .a-icon {
            min-width: 24px;
        }
    }

    &__sum {
        padding: 1rem;
        color: var(--color-primary);
        font-size: 1.125rem;
        font-weight: 500;

        @include media-breakpoint-up(md) {
            padding: 1rem calc(var(--button-space) + 1rem + 20px) 1rem 1rem;
        }
    }

    @include media-breakpoint-down(sm) {
        .a-accordion__header {
            gap: 8px;
        }
    }

    @include media-breakpoint-down(xs) {
        &__header, &__sum {
            font-size: 16px;
            line-height: 1.2;
        }
    }
}
</style>